class NvxHero extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;

    this.ready = true;
    this.abortController = new AbortController();
    this.slides = [...this.querySelectorAll('[data-slide]')];
    this.index = Math.max(0, this.slides.findIndex((slide) => slide.classList.contains('is-active')));
    this.auto = this.dataset.auto === 'true';
    this.delay = Number.parseInt(this.dataset.delay, 10) || 4000;
    this.paused = false;
    this.interactionPaused = false;
    this.loaded = document.readyState === 'complete';
    this.visible = !('IntersectionObserver' in window);
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.timer = null;
    this.mediaPromises = new Map();
    this.transitionToken = 0;
    this.autoplayReady = false;

    if (this.slides.length < 2) return;

    const { signal } = this.abortController;

    this.querySelector('[data-prev]')?.addEventListener(
      'click',
      () => void this.show(this.index - 1, true),
      { signal }
    );
    this.querySelector('[data-next]')?.addEventListener(
      'click',
      () => void this.show(this.index + 1, true),
      { signal }
    );
    this.querySelector('[data-toggle]')?.addEventListener(
      'click',
      () => this.toggle(),
      { signal }
    );

    this.addEventListener(
      'mouseenter',
      () => {
        this.interactionPaused = true;
        this.stop();
      },
      { signal }
    );
    this.addEventListener(
      'mouseleave',
      () => {
        this.interactionPaused = false;
        this.start();
      },
      { signal }
    );
    this.addEventListener(
      'focusin',
      () => {
        this.interactionPaused = true;
        this.stop();
      },
      { signal }
    );
    this.addEventListener(
      'focusout',
      (event) => {
        if (!this.contains(event.relatedTarget)) {
          this.interactionPaused = false;
          this.start();
        }
      },
      { signal }
    );

    let touchStartX = 0;
    this.addEventListener(
      'touchstart',
      (event) => {
        touchStartX = event.touches[0]?.clientX || 0;
        this.stop();
      },
      { passive: true, signal }
    );
    this.addEventListener(
      'touchend',
      (event) => {
        const distance = (event.changedTouches[0]?.clientX || 0) - touchStartX;
        if (Math.abs(distance) > 45) {
          void this.show(distance > 0 ? this.index - 1 : this.index + 1, true);
        } else {
          this.start();
        }
      },
      { passive: true, signal }
    );

    document.addEventListener(
      'visibilitychange',
      () => {
        if (document.hidden) this.stop();
        else this.start();
      },
      { signal }
    );

    if ('IntersectionObserver' in window) {
      this.visibilityObserver = new IntersectionObserver(
        ([entry]) => {
          this.visible = Boolean(entry?.isIntersecting && entry.intersectionRatio >= 0.15);
          if (this.visible) this.start();
          else this.stop();
        },
        { threshold: [0, 0.15] }
      );
      this.visibilityObserver.observe(this);
    }

    const afterLoad = () => {
      this.loaded = true;
      void this.prepareInitialBuffer();
    };

    if (this.loaded) {
      afterLoad();
    } else {
      window.addEventListener('load', afterLoad, { once: true, signal });
    }
  }

  disconnectedCallback() {
    this.stop();
    this.transitionToken += 1;
    this.visibilityObserver?.disconnect();
    this.abortController?.abort();
  }

  runWhenIdle(callback, timeout = 1600) {
    const invoke = () => {
      if (!this.isConnected) return;
      void callback();
    };

    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(invoke, { timeout });
    } else {
      window.setTimeout(invoke, Math.min(timeout, 900));
    }
  }

  async prepareInitialBuffer() {
    await this.waitForMedia(this.slides[this.index]);
    if (!this.isConnected) return;

    const nextIndex = (this.index + 1) % this.slides.length;
    const previousIndex = (this.index - 1 + this.slides.length) % this.slides.length;

    this.runWhenIdle(async () => {
      await this.hydrate(nextIndex);
      if (!this.isConnected) return;

      this.autoplayReady = true;
      this.start();

      this.runWhenIdle(async () => {
        await this.hydrate(previousIndex);
        if (!this.isConnected) return;

        void this.hydrate((this.index + 2) % this.slides.length);
      }, 2400);
    }, 1400);
  }

  hydrate(index) {
    if (this.mediaPromises.has(index)) return this.mediaPromises.get(index);

    const promise = (async () => {
      const slide = this.slides[index];
      if (!slide) return;

      const slot = slide.querySelector('[data-media-slot]');
      if (!slot) return;

      if (slot.dataset.hydrated !== 'true') {
        const template = slot.querySelector('[data-media-template]');

        if (template) {
          const fragment = template.content.cloneNode(true);
          fragment.querySelectorAll('img').forEach((image) => {
            image.loading = 'eager';
            image.fetchPriority = 'low';
            image.decoding = 'async';
          });
          slot.append(fragment);
          template.remove();
        }

        slot.dataset.hydrated = 'true';
      }

      await this.waitForMedia(slide);
    })();

    this.mediaPromises.set(index, promise);
    return promise;
  }

  async waitForMedia(slide) {
    const image = slide.querySelector('img');
    if (!image) return;

    if (!image.complete) {
      await Promise.race([
        new Promise((resolve) => {
          image.addEventListener('load', resolve, { once: true });
          image.addEventListener('error', resolve, { once: true });
        }),
        new Promise((resolve) => window.setTimeout(resolve, 6000)),
      ]);
    }

    if (typeof image.decode === 'function' && image.naturalWidth > 0) {
      try {
        await image.decode();
      } catch {
        // A decoded frame is an enhancement; a loaded image can still display safely.
      }
    }
  }

  async show(nextIndex, userInitiated = false) {
    const normalizedIndex = (nextIndex + this.slides.length) % this.slides.length;
    if (normalizedIndex === this.index) return;

    const transitionToken = ++this.transitionToken;
    this.stop();
    await this.hydrate(normalizedIndex);

    if (transitionToken !== this.transitionToken || !this.isConnected) return;

    this.index = normalizedIndex;

    this.slides.forEach((slide, index) => {
      const active = index === this.index;
      slide.classList.toggle('is-active', active);
      slide.setAttribute('aria-hidden', active ? 'false' : 'true');

      if (active) slide.removeAttribute('inert');
      else slide.setAttribute('inert', '');
    });

    void this.prepareAhead();

    if (userInitiated) this.start();
  }

  async prepareAhead() {
    const nextIndex = (this.index + 1) % this.slides.length;
    const afterNextIndex = (this.index + 2) % this.slides.length;

    await this.hydrate(nextIndex);
    void this.hydrate(afterNextIndex);
  }

  start() {
    this.stop();

    if (
      !this.loaded ||
      !this.autoplayReady ||
      !this.visible ||
      !this.auto ||
      this.paused ||
      this.interactionPaused ||
      this.reduceMotion ||
      document.hidden
    ) {
      return;
    }

    this.timer = window.setTimeout(() => {
      this.timer = null;
      this.show(this.index + 1).then(() => this.start());
    }, this.delay);
  }

  stop() {
    if (this.timer !== null) {
      window.clearTimeout(this.timer);
      this.timer = null;
    }
  }

  toggle() {
    this.paused = !this.paused;
    this.classList.toggle('is-paused', this.paused);

    const button = this.querySelector('[data-toggle]');
    const label = this.paused ? 'Play slideshow' : 'Pause slideshow';
    button?.setAttribute('aria-pressed', this.paused ? 'true' : 'false');
    button?.setAttribute('aria-label', label);

    const visibleLabel = button?.querySelector('[data-toggle-label]');
    if (visibleLabel) visibleLabel.textContent = label;

    if (this.paused) this.stop();
    else this.start();
  }
}

if (!customElements.get('nvx-hero')) {
  customElements.define('nvx-hero', NvxHero);
}
